import { ColumnsSettingsViewDict } from '../typings/common';
import { GridData, GridExpansion } from '../typings/grid';
import { LogSourceParams } from '../typings/log';

export const SET_CLIENT_DATA = 'SET_CLIENT_DATA';
export type SET_CLIENT_DATA = typeof SET_CLIENT_DATA;

export const SET_LOG_DATA = 'SET_LOG_DATA';
export type SET_LOG_DATA = typeof SET_LOG_DATA;

export const SET_STUB_DATA = 'SET_STUB_DATA';
export type SET_STUB_DATA = typeof SET_STUB_DATA;

export const UPDATE_LOG_DATA = 'UPDATE_LOG_DATA';
export type UPDATE_LOG_DATA = typeof UPDATE_LOG_DATA;

export const UPDATE_LOG_SOURCE_PARAMS = 'UPDATE_LOG_SOURCE_PARAMS';
export type UPDATE_LOG_SOURCE_PARAMS = typeof UPDATE_LOG_SOURCE_PARAMS;

export const EXPAND_ROW = 'EXPAND_ROW';
export type EXPAND_ROW = typeof EXPAND_ROW;

export const EXPAND_ALL_ROWS = 'EXPAND_ALL_ROWS';
export type EXPAND_ALL_ROWS = typeof EXPAND_ALL_ROWS;

export const CHANGE_COLUMNS_LOG_VISIBILITY = 'CHANGE_COLUMNS_GRID_VISIBILITY';
export type CHANGE_COLUMNS_LOG_VISIBILITY = typeof CHANGE_COLUMNS_LOG_VISIBILITY;

export const CHANGE_COLUMNS_LOG_ORDER = 'CHANGE_COLUMNS_LOG_ORDER';
export type CHANGE_COLUMNS_LOG_ORDER = typeof CHANGE_COLUMNS_LOG_ORDER;

export type UPDATE_LOG_COLUMNS_WIDTH = 'UPDATE_LOG_COLUMNS_WIDTH';
export const UPDATE_LOG_COLUMNS_WIDTH = 'UPDATE_LOG_COLUMNS_WIDTH';

export const CHANGE_MAX_HEIGHT_CLEARED = 'CHANGE_MAX_HEIGHT_CLEARED';
export type CHANGE_MAX_HEIGHT_CLEARED = typeof CHANGE_MAX_HEIGHT_CLEARED;

export interface SetClientData {
    type: SET_CLIENT_DATA;
    login: string;
    isClient: boolean;
}

export interface UpdateColumnsWidthData {
    type: UPDATE_LOG_COLUMNS_WIDTH;
    columns: ColumnsSettingsViewDict;
    changedValueWidth?: number;
}

export function updateColumnsWidthData(
    columns: ColumnsSettingsViewDict,
    changedValueWidth?: number
): UpdateColumnsWidthData {
    return {
        type: UPDATE_LOG_COLUMNS_WIDTH,
        columns,
        changedValueWidth
    };
}

export interface ColumnsVisibilityChange {
    type: CHANGE_COLUMNS_LOG_VISIBILITY;
    columns: ColumnsSettingsViewDict;
}

export function changeColumnsVisibility(
    columns: ColumnsSettingsViewDict
): ColumnsVisibilityChange {
    return {
        type: CHANGE_COLUMNS_LOG_VISIBILITY,
        columns
    };
}

export interface ColumnsOrderChange {
    type: CHANGE_COLUMNS_LOG_ORDER;
    columnOrder: Array<string>;
}

export function changeColumnsOrder(
    columnOrder: Array<string>
): ColumnsOrderChange {
    return {
        type: CHANGE_COLUMNS_LOG_ORDER,
        columnOrder
    };
}

export interface SetLogData {
    type: SET_LOG_DATA;
    gridData: GridData;
    nextPageToken: string | null;
    rowsCanExpand: Map<number, boolean>;
}

export interface UpdateLogData {
    type: UPDATE_LOG_DATA;
    gridData: GridData;
    nextPageToken: string | null;
    rowsCanExpand: Map<number, boolean>;
}

export interface SetStubData {
    type: SET_STUB_DATA;
}

export interface UpdateLogSourceParams {
    type: UPDATE_LOG_SOURCE_PARAMS;
    params: LogSourceParams;
}

export interface ExpandRow {
    type: EXPAND_ROW;
    gridExpansion: GridExpansion;
}

export interface ExpandAllRows {
    type: EXPAND_ALL_ROWS;
    isExpanded: boolean;
}

export interface ChangeMaxHeightCleared {
    type: CHANGE_MAX_HEIGHT_CLEARED;
}

export function setClientData(login: string, isClient: boolean): SetClientData {
    return {
        type: SET_CLIENT_DATA,
        login: login,
        isClient: isClient
    };
}

export function setLogData(
    gridData: GridData,
    nextPageToken: string | null,
    rowsCanExpand: Map<number, boolean>
): SetLogData {
    return {
        type: SET_LOG_DATA,
        gridData: gridData,
        nextPageToken: nextPageToken,
        rowsCanExpand: rowsCanExpand
    };
}

export function updateLogData(
    gridData: GridData,
    nextPageToken: string | null,
    rowsCanExpand: Map<number, boolean>
): UpdateLogData {
    return {
        type: UPDATE_LOG_DATA,
        gridData: gridData,
        nextPageToken: nextPageToken,
        rowsCanExpand: rowsCanExpand
    };
}

export function setStubData(): SetStubData {
    return { type: SET_STUB_DATA };
}

export function updateLogSourceParams(
    params: LogSourceParams
): UpdateLogSourceParams {
    return {
        type: UPDATE_LOG_SOURCE_PARAMS,
        params: params
    };
}

export function expandRow(gridExpansion: GridExpansion): ExpandRow {
    return {
        type: EXPAND_ROW,
        gridExpansion: gridExpansion
    };
}

export function expandAllRows(isExpanded: boolean): ExpandAllRows {
    return {
        type: EXPAND_ALL_ROWS,
        isExpanded: isExpanded
    };
}

export const HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE =
    'HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE';
export type HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE = typeof HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE;

export interface HandleLogColumnsVisibilityChangeAction {
    type: HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE;
    columns: string[];
}

export function handleLogColumnsVisibilityChange(
    columns: string[]
): HandleLogColumnsVisibilityChangeAction {
    return {
        type: HANDLE_LOG_COLUMNS_VISIBILITY_CHANGE,
        columns
    };
}

export const HANDLE_LOG_COLUMNS_ORDER_CHANGE =
    'HANDLE_LOG_COLUMNS_ORDER_CHANGE';
export type HANDLE_LOG_COLUMNS_ORDER_CHANGE = typeof HANDLE_LOG_COLUMNS_ORDER_CHANGE;

export interface HandleLogColumnsOrderChangeAction {
    type: HANDLE_LOG_COLUMNS_ORDER_CHANGE;
    columnOrder: string[];
}

export function handleLogColumnsOrderChange(
    columnOrder: string[]
): HandleLogColumnsOrderChangeAction {
    return {
        type: HANDLE_LOG_COLUMNS_ORDER_CHANGE,
        columnOrder
    };
}

export const HANDLE_LOG_COLUMNS_WIDTH_UPDATE =
    'HANDLE_LOG_COLUMNS_WIDTH_UPDATE';
export type HANDLE_LOG_COLUMNS_WIDTH_UPDATE = typeof HANDLE_LOG_COLUMNS_WIDTH_UPDATE;

export interface HandleLogColumnsWidthUpdateAction {
    type: HANDLE_LOG_COLUMNS_WIDTH_UPDATE;
    width: number;
    key: string;
}

export function handleLogColumnsWidthUpdate(
    width: number,
    key: string
): HandleLogColumnsWidthUpdateAction {
    return {
        type: HANDLE_LOG_COLUMNS_WIDTH_UPDATE,
        width,
        key
    };
}

export function changeMaxHeightCleared(): ChangeMaxHeightCleared {
    return {
        type: CHANGE_MAX_HEIGHT_CLEARED
    };
}

export const UPDATE_COLUMNS_SETTINGS_LOG_STATE =
    'UPDATE_COLUMNS_SETTINGS_LOG_STATE';
export type UPDATE_COLUMNS_SETTINGS_LOG_STATE = typeof UPDATE_COLUMNS_SETTINGS_LOG_STATE;

export interface UpdateColumnsSettingsLogState {
    type: UPDATE_COLUMNS_SETTINGS_LOG_STATE;
    columnsSettings: ColumnsSettingsViewDict;
}

export const updateColumnsSettingsLogState = (
    columnsSettings: ColumnsSettingsViewDict
): UpdateColumnsSettingsLogState => {
    return {
        type: UPDATE_COLUMNS_SETTINGS_LOG_STATE,
        columnsSettings
    };
};

export type LogAction =
    | SetClientData
    | SetLogData
    | UpdateLogData
    | SetStubData
    | UpdateLogSourceParams
    | ExpandRow
    | ExpandAllRows
    | UpdateColumnsWidthData
    | ColumnsVisibilityChange
    | ColumnsOrderChange
    | ChangeMaxHeightCleared
    | HandleLogColumnsVisibilityChangeAction
    | HandleLogColumnsOrderChangeAction
    | HandleLogColumnsWidthUpdateAction
    | UpdateColumnsSettingsLogState;
